import { API_URL } from '../config';
import { getApiOwnerId } from '../services/api';
import { Building, FinancialSummary } from '@akfdev-com/shared/src/models';

function authHeaders(token: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  const ownerId = getApiOwnerId();
  if (ownerId) {
    headers['X-Clerk-Subdomain'] = ownerId;
  }
  return headers;
}

// Parses JSON but throws a clear error if the response is HTML (e.g. CloudFront
// error pages that masquerade as 200 when a route doesn't exist in API Gateway).
async function parseJsonResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(`API returned non-JSON response (HTTP ${response.status}) — the endpoint may not be deployed yet`);
  }
  return response.json() as Promise<T>;
}

/**
 * Fetch all buildings that the user has access to
 */
export const fetchUserBuildings = async (token: string): Promise<Building[]> => {
  try {
    const response = await fetch(`${API_URL}/buildings`, {
      headers: authHeaders(token),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return parseJsonResponse<Building[]>(response);
  } catch (error) {
    console.error('Error fetching buildings:', error);
    throw error;
  }
};

/**
 * Fetch a single building by ID
 */
export const fetchBuilding = async (buildingId: string, token: string): Promise<Building> => {
  try {
    const response = await fetch(`${API_URL}/buildings/${buildingId}`, {
      headers: authHeaders(token),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return parseJsonResponse<Building>(response);
  } catch (error) {
    console.error(`Error fetching building ${buildingId}:`, error);
    throw error;
  }
};

/**
 * Create a new building
 */
export const createBuilding = async (buildingData: Omit<Building, 'id' | 'createdAt' | 'updatedAt'>, token: string): Promise<Building> => {
  try {
    const response = await fetch(`${API_URL}/buildings`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(buildingData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return parseJsonResponse<Building>(response);
  } catch (error) {
    console.error('Error creating building:', error);
    throw error;
  }
};

/**
 * Update a building
 */
export const updateBuilding = async (buildingId: string, buildingData: Partial<Building>, token: string): Promise<Building> => {
  try {
    const response = await fetch(`${API_URL}/buildings/${buildingId}`, {
      method: 'PUT',
      headers: authHeaders(token),
      body: JSON.stringify(buildingData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return parseJsonResponse<Building>(response);
  } catch (error) {
    console.error(`Error updating building ${buildingId}:`, error);
    throw error;
  }
};

/**
 * Delete a building
 */
export const deleteBuilding = async (buildingId: string, token: string): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/buildings/${buildingId}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
  } catch (error) {
    console.error(`Error deleting building ${buildingId}:`, error);
    throw error;
  }
};

/**
 * Fetch the live aggregated financial summary for a building.
 * Returned monetary values are in pence (consistent with FinancialRecord.amount).
 * 
 * Note: If the backend returns an error (e.g., HTTP 500), this function throws
 * a descriptive error. The caller (e.g., BuildingDetails) should catch this
 * and handle gracefully (e.g., set summary to null).
 */
export const fetchBuildingFinancialSummary = async (
  buildingId: string,
  token: string,
): Promise<FinancialSummary> => {
  try {
    const response = await fetch(
      `${API_URL}/buildings/${buildingId}/financial-summary`,
      { headers: authHeaders(token) },
    );

    if (!response.ok) {
      console.error(
        `[fetchBuildingFinancialSummary] HTTP ${response.status} for building ${buildingId}`
      );
      // Throw descriptive error without attempting to parse response body
      throw new Error(
        `Failed to fetch financial summary: HTTP ${response.status}. ` +
        `The endpoint may be experiencing issues. Check server logs.`
      );
    }

    return parseJsonResponse<FinancialSummary>(response);
  } catch (error) {
    // Handle both Error objects and unknown exception types
    const errorMessage = error instanceof Error 
      ? error.message 
      : String(error);
    
    console.error(
      `[fetchBuildingFinancialSummary] Error for building ${buildingId}: ${errorMessage}`
    );
    
    // Re-throw with clear, non-technical message for UI display
    throw new Error(
      `Financial summary unavailable: ${errorMessage}`
    );
  }
};
